import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Chart from 'chart.js/auto';
import { useAuth } from '../contexts/AuthContext';
// hCaptcha guarded by sitekey; if not set, it won't render
import HCaptcha from '@hcaptcha/react-hcaptcha';
import LoadingAnimation from '../components/LoadingAnimation';
import ReferralSystem from '../components/ReferralSystem';  // ✅ CORRECT
import TokenDashboard from '../components/TokenDashboard';  // ✅ NEW


import GoogleSignIn from '../components/GoogleSignIn';

import {
    Mail,
    Lock,
    User,
    LogOut,
    Target,
    TrendingUp,
    Calendar,
    CalendarDays,
    FileText,
    Plus,
    Send,
    ArrowLeft,
    Globe,
    EyeOff,
    BarChart3,
    PieChart,
    Sparkles,
    Rocket,
    Award,
    Activity,
    Users,
    Eye,
    CheckCircle,
    Circle,
    Loader,
    Check,
    X,
    EyeIcon,
    AlertCircle,
    ChevronRight,
    ChevronLeft,
    RefreshCw, 
    Clock,
    Shield,
    AlertTriangle,
    Coins
} from 'lucide-react';


import './Login.css';

function Login() {
    
    const { isLoggedIn: authIsLoggedIn, login, loginWithGoogle, register, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [isRegistering, setIsRegistering] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(authIsLoggedIn);
    const [userStats, setUserStats] = useState(null);
    const [user, setUser] = useState(null);
    const [recentPredictions, setRecentPredictions] = useState([]);
    const [allPredictions, setAllPredictions] = useState([]);
    const [showAllPredictions, setShowAllPredictions] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingAll, setIsLoadingAll] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [showPassword, setShowPassword] = useState(false);
    const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
    const [emailValidation, setEmailValidation] = useState({ isValid: false, message: '', isChecking: false });
    const [usernameValidation, setUsernameValidation] = useState({ isValid: false, message: '', isChecking: false });
    const [passwordValidation, setPasswordValidation] = useState({ strength: 'weak', message: '', isValid: false });
    const [suggestedUsernames, setSuggestedUsernames] = useState([]);
    const [emailFocused, setEmailFocused] = useState(false);
    const [usernameFocused, setUsernameFocused] = useState(false);
    const [passwordFocused, setPasswordFocused] = useState(false);
    const [userReliability, setUserReliability] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [captchaValue, setCaptchaValue] = useState(null);
    const [captchaError, setCaptchaError] = useState('');
    const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);
    const captchaRef = useRef(null);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetMessage, setResetMessage] = useState('');
    const [showEmailAnimation, setShowEmailAnimation] = useState(false);
    
    // Initialize activeTab from URL query parameter or default to 'overview'
    const [activeTab, setActiveTab] = useState(() => {
        const tabFromUrl = new URLSearchParams(window.location.search).get('tab');
        if (tabFromUrl && ['overview', 'tphile', 'referrals'].includes(tabFromUrl)) {
            return tabFromUrl;
        }
        return 'overview';
    });

    // Handle tab change - updates both state and URL
    const handleTabChange = useCallback((tabName) => {
        if (['overview', 'tphile', 'referrals'].includes(tabName)) {
            setActiveTab(tabName);
            // Update URL with the new tab parameter using window.location
            const url = new URL(window.location);
            url.searchParams.set('tab', tabName);
            window.history.replaceState({}, '', url);
        }
    }, []);

    // Sync activeTab with URL when location changes (e.g., browser back/forward)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const tabFromUrl = params.get('tab');
        if (tabFromUrl && ['overview', 'tphile', 'referrals'].includes(tabFromUrl)) {
            if (tabFromUrl !== activeTab) {
                setActiveTab(tabFromUrl);
            }
        }
    }, [location.search, activeTab]);

    // Listen for browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            const params = new URLSearchParams(window.location.search);
            const tabFromUrl = params.get('tab');
            if (tabFromUrl && ['overview', 'tphile', 'referrals'].includes(tabFromUrl)) {
                setActiveTab(tabFromUrl);
            } else if (!tabFromUrl) {
                setActiveTab('overview');
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // 🐛 DEBUG: Log activeTab changes
    useEffect(() => {
        console.log('🔄 Active Tab Changed:', activeTab);
    }, [activeTab]);

    // Handle incoming navigation state (from VestingDetails "Back to Dashboard" button)
    useEffect(() => {
        try {
            // Safety check: Only process valid tab values
            const targetTab = location?.state?.activeTab;
            if (targetTab && (targetTab === 'tphile' || targetTab === 'overview' || targetTab === 'referrals')) {
                console.log('📍 Navigating to tab:', targetTab);
                handleTabChange(targetTab);
                // Clear the state to prevent re-triggering on re-renders
                window.history.replaceState({}, document.title);
            }
        } catch (error) {
            console.error('Error handling navigation state:', error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount


    
const handleForgotPassword = async (e) => {
  e.preventDefault();
  setIsLoading(true);
  setResetMessage('');

  try {
    // Build the correct URL based on environment
    const isDevelopment = window.location.hostname === 'localhost';
    const baseUrl = isDevelopment 
      ? 'http://localhost:3001/api'  // Development: absolute URL
      : process.env.REACT_APP_API_URL; // Production: relative URL
    
    const apiUrl = `${baseUrl}/forgot-password`;
    
    console.log('🔍 Environment:', isDevelopment ? 'development' : 'production');
    console.log('🔍 Base URL:', baseUrl);
    console.log('🔍 Making request to:', apiUrl);
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: resetEmail }),
    });

    console.log('🔍 Response status:', response.status);
    console.log('🔍 Response ok:', response.ok);
    
    // Check content type before parsing
    const contentType = response.headers.get('content-type');
    console.log('🔍 Response content-type:', contentType);
    
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      console.error('🔍 Non-JSON response received:', responseText);
      throw new Error(`Server returned non-JSON response: ${response.status}`);
    }

    const data = await response.json();
    console.log('🔍 Response data:', data);

    if (response.ok) {
      // Show email animation instead of just message
      setShowEmailAnimation(true);
      setResetEmail('');
      
      // Hide the animation after 3 seconds and show success message
      setTimeout(() => {
        setShowEmailAnimation(false);
        setResetMessage('Password reset link sent to your email!');
      }, 3000);
    } else {
      setResetMessage(data.message || 'Failed to send reset email');
    }
  } catch (error) {
    console.error('❌ Forgot password error:', error);
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      setResetMessage('Cannot connect to server. Please check if the backend is running.');
    } else {
      setResetMessage(error.response?.data?.message || 'Something went wrong. Please try again.');
    }
  } finally {
    setIsLoading(false);
  }
};
// Add to pages with scrolling issues
const ScrollPerformanceMonitor = () => {
  useEffect(() => {
    let scrollCount = 0;
    let lastScrollTime = performance.now();
    
    const handleScroll = () => {
      scrollCount++;
      const currentTime = performance.now();
      
      // Measure scroll frequency
      if (currentTime - lastScrollTime > 1000) {
        console.log(`📊 Scroll events per second: ${scrollCount}`);
        
        if (scrollCount > 100) {
          console.warn('🔴 Too many scroll events - consider throttling');
        }
        
        scrollCount = 0;
        lastScrollTime = currentTime;
      }
    };
    
    // Throttled scroll listener
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', throttledScroll, { passive: true });
    
    return () => window.removeEventListener('scroll', throttledScroll);
  }, []);
  
  return null;
};





    const [verificationState, setVerificationState] = useState({
        showEmailSent: false,
        showResendOption: false,
        isResending: false,
        email: '',
        username: '',
        message: ''
    });
    const [urlParams, setUrlParams] = useState({
        verification: null,
        token: null,
        username: null,
        message: null
    });
    const [flagModal, setFlagModal] = useState({
        isOpen: false,
        reason: '',
        predictionText: ''
    });


    const [qualityModal, setQualityModal] = useState({
        isOpen: false,
        predictionData: null,
        suggestions: []
    });

    const dailyActivityChartRef = useRef(null);
    const categoryChartRef = useRef(null);
    const dailyActivityChartInstance = useRef(null);
    const categoryChartInstance = useRef(null);
    const emailCheckTimeout = useRef(null);
    const usernameCheckTimeout = useRef(null);

    // Get token from AuthContext at the component level
    const { token } = useAuth();
        // ADD THE DEBUG RIGHT HERE - after all declarations but before functions
        console.log('🔍 Debug Info:', {
            isLoggedIn,
            userStats,
            user,
            isLoading,
            showSuccessAnimation,
            authIsLoggedIn,
            token,
            localStorage_token: localStorage.getItem('token'),
            localStorage_username: localStorage.getItem('username'),
            localStorage_user: localStorage.getItem('user'),
            sessionStorage_token: sessionStorage.getItem('token')
        });
    

    const destroyCharts = useCallback(() => {
        try {
            if (dailyActivityChartInstance.current) {
                dailyActivityChartInstance.current.destroy();
                dailyActivityChartInstance.current = null;
            }
        } catch (error) {
            console.log('Daily chart cleanup error (safe to ignore):', error);
        }
        
        try {
            if (categoryChartInstance.current) {
                categoryChartInstance.current.destroy();
                categoryChartInstance.current = null;
            }
        } catch (error) {
            console.log('Category chart cleanup error (safe to ignore):', error);
        }
    }, []);

    const fetchUserActivity = async (username) => {
        try {
            console.log('🔍 Fetching activity for user:', username);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/debug/daily-activity/${username}`);
            console.log('📊 Activity data received:', response.data);
            return response.data.dailyActivity;
        } catch (error) {
            console.error('❌ Error fetching user activity:', error);
            return [];
        }
    };

    const validateEmail = async (emailValue) => {
        if (!emailValue) {
            setEmailValidation({ isValid: false, message: '', isChecking: false });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailValue)) {
            setEmailValidation({ 
                isValid: false, 
                message: 'Please enter a valid email address', 
                isChecking: false 
            });
            return;
        }

        setEmailValidation({ isValid: false, message: '', isChecking: true });

        try {
            console.log('🔍 Checking email availability for:', emailValue);
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/check-email`, { email: emailValue });
            console.log('📧 Email check response:', response.data);
            
            if (response.data.exists) {
                setEmailValidation({ 
                    isValid: false, 
                    message: 'Email already registered', 
                    isChecking: false 
                });
            } else {
                setEmailValidation({ 
                    isValid: true, 
                    message: 'Email available', 
                    isChecking: false 
                });
                generateUsernameSuggestions(emailValue);
            }
        } catch (error) {
            console.error('❌ Email validation error:', error);
            setEmailValidation({ 
                isValid: false, 
                message: 'Unable to verify email. Please try again.', 
                isChecking: false 
            });
        }
    };

    const validateUsername = async (usernameValue) => {
        if (!usernameValue) {
            setUsernameValidation({ isValid: false, message: '', isChecking: false });
            return;
        }

        const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
        if (!usernameRegex.test(usernameValue)) {
            setUsernameValidation({ 
                isValid: false, 
                message: 'Username must be 3-20 characters, letters, numbers and underscores only', 
                isChecking: false 
            });
            return;
        }

        setUsernameValidation({ isValid: false, message: '', isChecking: true });
        
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/check-username`, { username: usernameValue });
            if (response.data.exists) {
                setUsernameValidation({ isValid: false, message: 'Username already taken', isChecking: false });
            } else {
                setUsernameValidation({ isValid: true, message: 'Username available', isChecking: false });
            }
        } catch (error) {
            setUsernameValidation({ isValid: false, message: 'Error checking username availability', isChecking: false });
        }
    };

    const validatePassword = (passwordValue) => {
        if (!passwordValue) {
            setPasswordValidation({ strength: 'weak', message: '', isValid: false });
            return;
        }

        let score = 0;
        let message = '';
        
        if (passwordValue.length >= 8) score++;
        if (/[a-z]/.test(passwordValue)) score++;
        if (/[A-Z]/.test(passwordValue)) score++;
        if (/[0-9]/.test(passwordValue)) score++;
        if (/[^A-Za-z0-9]/.test(passwordValue)) score++;

        if (score < 3) {
            setPasswordValidation({ 
                strength: 'weak', 
                message: 'Password too weak - add more characters, numbers, or symbols', 
                isValid: false 
            });
        } else if (score < 4) {
            setPasswordValidation({ 
                strength: 'medium', 
                message: 'Good password - consider adding symbols for extra security', 
                isValid: true 
            });
        } else {
            setPasswordValidation({ 
                strength: 'strong', 
                message: 'Strong password!', 
                isValid: true 
            });
        }
    };

    const generateUsernameSuggestions = (emailValue) => {
        const baseName = emailValue.split('@')[0];
        const suggestions = [
            baseName,
            `${baseName}${Math.floor(Math.random() * 99) + 1}`,
            `${baseName}_${new Date().getFullYear()}`,
            `${baseName}_predictor`,
            `future_${baseName}`
        ].filter(name => name.length <= 20);
        
        setSuggestedUsernames(suggestions);
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        
        if (emailCheckTimeout.current) {
            clearTimeout(emailCheckTimeout.current);
        }
        
        emailCheckTimeout.current = setTimeout(() => {
            if (isRegistering) {
                validateEmail(value);
            }
        }, 500);
    };

    const handleUsernameChange = (e) => {
        const value = e.target.value;
        setUsername(value);
        
        if (usernameCheckTimeout.current) {
            clearTimeout(usernameCheckTimeout.current);
        }
        
        usernameCheckTimeout.current = setTimeout(() => {
            if (isRegistering) {
                validateUsername(value);
            }
        }, 500);
    };

    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setPassword(value);
        
        if (isRegistering) {
            validatePassword(value);
        }
    };

    const canProceedToStep2 = emailValidation.isValid && !emailValidation.isChecking;
    const canProceedToStep3 = usernameValidation.isValid && !usernameValidation.isChecking;
    const canSubmit = passwordValidation.isValid;

    const nextStep = () => {
        if (currentStep === 1 && canProceedToStep2) {
            setCurrentStep(2);
        } else if (currentStep === 2 && canProceedToStep3) {
            setCurrentStep(3);
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const toggleRegistrationMode = () => {
        setIsRegistering(!isRegistering);
        setCurrentStep(1);
        setEmail('');
        setUsername('');
        setPassword('');
        setEmailValidation({ isValid: false, message: '', isChecking: false });
        setUsernameValidation({ isValid: false, message: '', isChecking: false });
        setPasswordValidation({ strength: 'weak', message: '', isValid: false });
        setSuggestedUsernames([]);
        setShowSuccessAnimation(false);
    };

    const createDailyActivityChart = useCallback(async () => {
        if (!dailyActivityChartRef.current || !user?.username) {
            console.log('❌ Missing chart ref or username');
            return;
        }
        
        if (dailyActivityChartInstance.current) {
            try {
                dailyActivityChartInstance.current.destroy();
                dailyActivityChartInstance.current = null;
            } catch (error) {
                console.log('Error destroying existing chart:', error);
            }
        }

        if (!dailyActivityChartRef.current || !dailyActivityChartRef.current.parentNode) {
            console.log('Canvas element not attached to DOM');
            return;
        }

        try {
            const activityData = await fetchUserActivity(user.username);
            console.log('📈 Creating chart with data:', activityData);

            if (!activityData || activityData.length === 0) {
                console.log('⚠️ No activity data available for chart');
                return;
            }

            const ctx = dailyActivityChartRef.current.getContext('2d');
            dailyActivityChartInstance.current = new Chart(ctx, {
                type: 'line',
                data: {
                    labels: activityData.map(d => {
                        const date = new Date(d.date);
                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }),
                    datasets: [{
                        label: 'Daily Predictions',
                        data: activityData.map(d => parseInt(d.count)),
                        borderColor: '#ffd700',
                        backgroundColor: 'rgba(255, 215, 0, 0.1)',
                        borderWidth: 3,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: '#ffd700',
                        pointBorderColor: '#000',
                        pointBorderWidth: 2,
                        pointRadius: 6
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: {
                        duration: 1000,
                        easing: 'easeInOutCubic'
                    },
                    plugins: {
                        legend: {
                            labels: {
                                color: '#ffd700',
                                font: { 
                                    size: 14,
                                    family: 'Inter, system-ui, sans-serif',
                                    weight: '500'
                                }
                            }
                        },
                        tooltip: {
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            titleColor: '#ffd700',
                            bodyColor: '#ffffff',
                            borderColor: '#ffd700',
                            borderWidth: 1,
                            cornerRadius: 12,
                            padding: 12
                        }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: {
                                color: '#cccccc',
                                stepSize: 1,
                                font: {
                                    size: 12,
                                    family: 'Inter, system-ui, sans-serif'
                                }
                            },
                            grid: {
                                color: 'rgba(255, 215, 0, 0.1)',
                                lineWidth: 1
                            },
                            border: {
                                color: 'rgba(255, 255, 255, 0.1)'
                            }
                        },
                        x: {
                            ticks: {
                                color: '#cccccc',
                                font: {
                                    size: 12,
                                    family: 'Inter, system-ui, sans-serif'
                                }
                            },
                            grid: {
                                color: 'rgba(255, 215, 0, 0.1)',
                                lineWidth: 1
                            },
                            border: {
                                color: 'rgba(255, 255, 255, 0.1)'
                            }
                        }
                    },
                    elements: {
                        point: {
                            radius: 6,
                            hoverRadius: 8,
                            borderWidth: 2,
                            backgroundColor: '#ffd700',
                            borderColor: '#000000'
                        },
                        line: {
                            tension: 0.4,
                            borderWidth: 3
                        }
                    }
                }
            });
        } catch (error) {
            console.error('❌ Error creating daily activity chart:', error);
        }
    }, [user?.username]);

    const createCategoryChart = useCallback(() => {
        if (!categoryChartRef.current || !userStats?.categoryDistribution) return;
        
        if (categoryChartInstance.current) {
            try {
                categoryChartInstance.current.destroy();
                categoryChartInstance.current = null;
            } catch (error) {
                console.log('Error destroying existing chart:', error);
            }
        }

        if (!categoryChartRef.current || !categoryChartRef.current.parentNode) {
            console.log('Canvas element not attached to DOM');
            return;
        }

        try {
            const ctx = categoryChartRef.current.getContext('2d');
            categoryChartInstance.current = new Chart(ctx, {
                type: 'doughnut',
                data: {
                    labels: userStats.categoryDistribution.map(c => c.category),
                    datasets: [{
                        data: userStats.categoryDistribution.map(c => c.count),
                        backgroundColor: [
                            '#ffd700', '#ffed4a', '#daa520', '#ffc107', 
                            '#ffdf00', '#ffce54', '#f39c12', '#f1c40f',
                            '#e67e22', '#d35400'
                        ],
                        borderColor: '#1a1a1a',
                        borderWidth: 2,
                        hoverOffset: 15
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '60%',
                    animation: {
                        duration: 1000,
                        easing: 'easeInOutCubic'
                    },
                    plugins: {
                        legend: {
                            position: 'bottom',
                            labels: {
                                color: '#ffffff',
                                padding: 20,
                                font: { 
                                    size: 12,
                                    family: 'Inter, system-ui, sans-serif',
                                    weight: '500'
                                },
                                usePointStyle: true,
                                pointStyle: 'circle'
                            }
                        },
                        tooltip: {
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            titleColor: '#ffd700',
                            bodyColor: '#ffffff',
                            borderColor: '#ffd700',
                            borderWidth: 1,
                            cornerRadius: 12,
                            padding: 12
                        }
                    },
                    elements: {
                        arc: {
                            borderWidth: 2,
                            borderColor: 'rgba(0, 0, 0, 0.8)'
                        }
                    }
                }
            });
        } catch (error) {
            console.error('Error creating category chart:', error);
        }
    }, [userStats]);
    

const fetchUserData = async (token) => {
    console.log('🔄 fetchUserData called with token:', token ? 'Present' : 'Missing');
    setIsLoading(true);
    
    try {
        if (!token) {
            console.log('❌ No token provided to fetchUserData');
            setIsLoading(false);
            return;
        }

        console.log('📡 Making initial API calls...');
        
        // Fetch user data first to get username
        const userResponse = await axios.get(`${process.env.REACT_APP_API_URL}/user`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        const userData = userResponse.data;
        console.log('👤 User data received:', userData);

        // Now fetch stats and predictions in parallel
        const [statsResponse, predictionsResponse] = await Promise.all([
            axios.get(`${process.env.REACT_APP_API_URL}/auth/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get(`${process.env.REACT_APP_API_URL}/my-predictions`, {
                headers: { Authorization: `Bearer ${token}` }
            })
        ]);

        console.log('📊 Stats Response:', statsResponse.data);

        // Get reliability from stats response
        let reliabilityResponse = null;
        try {
            console.log('📡 Fetching reliability for:', userData.username);
            reliabilityResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}/user/${userData.username}/reliability`, 
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } catch (err) {
            console.log('⚠️ Reliability fetch failed:', err.response?.data?.message);
        }

        console.log('✅ All API responses received');

        // Set user stats with proper structure
        if (statsResponse.data.success) {
            setUserStats({
                totalPredictions: statsResponse.data.totalPredictions || 0,
                categoryDistribution: statsResponse.data.categoryDistribution || [],
                dailyActivity: statsResponse.data.dailyActivity || [],
                flaggedCount: statsResponse.data.flaggedCount || 0
            });
            console.log('✅ UserStats set:', {
                total: statsResponse.data.totalPredictions,
                categories: statsResponse.data.categoryDistribution?.length,
                dailyDays: statsResponse.data.dailyActivity?.length
            });
        }

        setUser(userData);
        setRecentPredictions(predictionsResponse.data.slice(0, 5));
        setAllPredictions(predictionsResponse.data);
        
        // Set reliability data
        if (reliabilityResponse?.data?.reliability) {
            setUserReliability(reliabilityResponse.data.reliability);
            console.log('✅ Reliability data set');
        } else if (statsResponse.data?.user_reliability) {
            setUserReliability(statsResponse.data.user_reliability);
            console.log('✅ Reliability from stats');
        }
        
    } catch (err) {
        console.error('❌ Error fetching user data:', err);
        console.error('❌ Error details:', {
            message: err.message,
            status: err.response?.status,
            data: err.response?.data
        });
        
        if (err.response?.status === 401 || err.response?.status === 403) {
            localStorage.removeItem('token');
            sessionStorage.removeItem('token');
            setIsLoggedIn(false);
        }
    } finally {
        console.log('🏁 fetchUserData completed');
        setIsLoading(false);
    }
};

    

    const handleEmailVerificationReturn = useCallback(async (verification, token, username, message) => {
        console.log('🎯 Processing verification return:', { verification, token, username, message });
        
        if (verification === 'success' && token) {
            try {
                console.log('✅ Email verification successful - Auto logging in...');
                localStorage.setItem('token', token);
                if (username) {
                    console.log('👤 Storing username:', username);
                    localStorage.setItem('username', decodeURIComponent(username));
                }

                window.history.replaceState({}, document.title, window.location.pathname);
                console.log('🔄 Setting login state and fetching data...');
                setIsLoggedIn(true);
                await fetchUserData(token);

                console.log('🎉 Auto-login complete!');
            } catch (error) {
                console.error('❌ Auto-login after verification failed:', error);
                setVerificationState(prev => ({
                    ...prev,
                    message: 'Verification successful but login failed. Please try logging in manually.'
                }));
            }
        } else if (verification === 'expired') {
            console.log('⏰ Verification expired');
            setVerificationState({
                showEmailSent: false,
                showResendOption: true,
                isResending: false,
                email: '',
                username: '',
                message: 'Verification link expired. Please request a new one.'
            });
        } else if (verification === 'error') {
            console.log('❌ Verification error:', message);
            setVerificationState({
                showEmailSent: false,
                showResendOption: true,
                isResending: false,
                email: '',
                username: '',
                message: 'Verification failed. Please try again or request a new link.'
            });
        }
    }, []);

    const handleResendVerification = async () => {
        if (!verificationState.email) {
            setVerificationState(prev => ({
                ...prev,
                message: 'Email address is required to resend verification.'
            }));
            return;
        }
    
        setVerificationState(prev => ({ ...prev, isResending: true, message: '' }));
    
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/resend-verification`, {
                email: verificationState.email
            });
    
            setVerificationState(prev => ({
                ...prev,
                isResending: false,
                showResendOption: false,
                showEmailSent: true,
                message: 'Verification email sent! Please check your inbox and spam folder.'
            }));
    
            console.log('✅ Verification email resent');
        } catch (error) {
            console.error('❌ Resend verification error:', error);
            setVerificationState(prev => ({
                ...prev,
                isResending: false,
                message: error.response?.data?.message || 'Failed to resend verification email. Please try again.'
            }));
        }
    };
    // Handle /signup route and referral codes
useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    const currentPath = window.location.pathname;
    
    console.log('🔍 Route Handler:', { path: currentPath, refCode });
    
    // Auto-switch to registration mode if on /signup or has referral code
    if (currentPath === '/signup' || refCode) {
        console.log('✅ Switching to registration mode');
        setIsRegistering(true);
    }
    
    // Store referral code for use during registration
    if (refCode) {
        console.log('💾 Storing referral code:', refCode);
        sessionStorage.setItem('referral_code', refCode);
    }
}, []);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const verification = searchParams.get('verification');
        const token = searchParams.get('token');
        const username = searchParams.get('username');
        const message = searchParams.get('message');

        console.log('🔍 Current URL:', window.location.href);
        console.log('🔍 URL Params:', { verification, token, username, message });

        if (verification && !window.verificationProcessed) {
            console.log('📧 Found verification params - processing...');
            window.verificationProcessed = true;
            setUrlParams({ verification, token, username, message });
            handleEmailVerificationReturn(verification, token, username, message);
        } else if (verification && window.verificationProcessed) {
            console.log('⚠️ Verification already processed - skipping');
        } else {
            console.log('ℹ️ No verification params found');
        }
    }, [handleEmailVerificationReturn]);

    useEffect(() => {
        const localToken = localStorage.getItem('token') || sessionStorage.getItem('token');
        
        if (localToken && !userStats && !user) {
            console.log('🔄 Token found, setting logged in and fetching data...');
            setIsLoggedIn(true);
            fetchUserData(localToken);
        } else if (localToken && isLoggedIn && !userStats && !user) {
            // Backup: if somehow we're logged in but missing data
            console.log('🔄 Logged in but missing data - refetching...');
            fetchUserData(localToken);
        }
        
        return () => {
            if (dailyActivityChartRef.current || categoryChartRef.current) {
                destroyCharts();
            }
        };
    }, [destroyCharts, isLoggedIn, userStats, user]); // Added dependencies
    

    useEffect(() => {
        setIsLoggedIn(authIsLoggedIn);
    }, [authIsLoggedIn]);

    const handleViewAllPredictions = async () => {
        if (!showAllPredictions && allPredictions.length === 0) {
            setIsLoadingAll(true);
            try {
                const localToken = localStorage.getItem('token') || sessionStorage.getItem('token');
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/my-predictions`, {
                    headers: { Authorization: `Bearer ${localToken}` }
                });
                setAllPredictions(response.data);
            } catch (err) {
                console.error('Error fetching all predictions:', err);
            } finally {
                setIsLoadingAll(false);
            }
        }
        setShowAllPredictions(!showAllPredictions);
    };

    const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate CAPTCHA for login (not registration)
    if (!isRegistering && !captchaValue) {
        setCaptchaError('Please complete the security verification');
        return;
    }

    setIsLoading(true);
    setErrorMessage(''); // Clear previous errors

    // In handleSubmit function, replace the registration block (around lines 432-470)
    if (isRegistering) {
        try {
            console.log('🚀 Starting registration process...');
            const result = await register({
                email,
                username,
                password,
                referral_code: sessionStorage.getItem('referral_code') || undefined
            }, rememberMe);
            

            console.log('📧 Registration API response:', result);

            if (result.success) {
                if (result.requiresVerification) {
                    console.log('✅ Registration successful - Email verification required');
                    setVerificationState({
                        showEmailSent: true,
                        showResendOption: false,
                        isResending: false,
                        email: email,
                        username: username,
                        message: result.message || 'Account created! Please check your email to verify your account before logging in.'
                    });
                    // Reset form but keep user in registration mode for verification
                    setPassword('');
                    setCurrentStep(1);
                    setEmailValidation({ isValid: false, message: '', isChecking: false });
                    setUsernameValidation({ isValid: false, message: '', isChecking: false });
                    setPasswordValidation({ strength: 'weak', message: '', isValid: false });
                } else {
                    // Direct login without verification (fallback)
                    console.log('⚠️ Registration successful - No verification required (fallback)');
                    setShowSuccessAnimation(true);
                    setTimeout(() => {
                        setShowSuccessAnimation(false);
                        if (result.token) {
                            fetchUserData(result.token);
                        }
                    }, 2500);
                }
            } else {
                throw new Error(result.message || 'Registration failed');
            }
        } catch (err) {
            console.error('❌ Registration error:', err);
            setShowSuccessAnimation(false);
            setErrorMessage(err.message || 'Registration failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    } else {
        // LOGIN LOGIC WITH HCAPTCHA
        try {
            console.log('🔑 Starting login process with hCaptcha...');
            console.log('📤 Sending login data:', { 
                email: email || null, 
                username: username || null, 
                hasPassword: !!password,
                hasCaptcha: !!captchaValue
            });

            // Create login data with hCaptcha token
            const loginData = {
                email: email || null,
                username: username || null,
                password,
                captcha: captchaValue // Add hCaptcha token
            };

            const result = await login(loginData, rememberMe);

            console.log('🔐 Login API response:', result);

            if (result.success) {
                console.log('✅ Login successful');
                setShowSuccessAnimation(true);
                setTimeout(() => {
                    setShowSuccessAnimation(false);
                    if (result.token) {
                        fetchUserData(result.token);
                    }
                }, 1500);
            } else if (result.requiresVerification) {
                console.log('📧 Login blocked - Email verification required');
                setVerificationState({
                    showEmailSent: false,
                    showResendOption: true,
                    isResending: false,
                    email: result.email || email || username,
                    username: '',
                    message: result.message || 'Please verify your email address before logging in. Check your inbox for the verification link.'
                });
                resetCaptcha(); // Reset CAPTCHA on verification error
            } else {
                throw new Error(result.message || 'Login failed');
            }
        } catch (err) {
            console.error('❌ Login error:', err);
            setShowSuccessAnimation(false);
            resetCaptcha(); // Reset CAPTCHA on any error
            
            // Handle different error types
            if (err.response?.status === 500) {
                setErrorMessage('Server error occurred. Please try again later.');
            } else if (err.response?.status === 403) {
                // Email verification required - this should be handled above
                setErrorMessage('Email verification required. Please check your inbox.');
            } else if (err.response?.data?.message?.includes('Security verification')) {
                setErrorMessage('Security verification failed. Please complete the CAPTCHA and try again.');
                setCaptchaError('CAPTCHA verification failed, please try again');
            } else {
                setErrorMessage(err.response?.data?.message || err.message || 'Login failed. Please check your credentials.');
            }
        } finally {
            setIsLoading(false);
        }
    }
};
    

    const handleLogout = () => {
        // Use AuthContext logout to clear all auth state
        logout();
        // Clear local component state
        setIsLoggedIn(false);
        setUserStats(null);
        setUserReliability(null);
        setUser(null);
        setRecentPredictions([]);
        setAllPredictions([]);
        // Redirect to login page
        window.location.href = '/login';
    };

    const getReliabilityClass = (score) => {
        if (score >= 80) return 'excellent';
        if (score >= 60) return 'good';
        if (score >= 40) return 'fair';
        return 'poor';
    };

    const getPerformanceInsights = () => {
        if (!userReliability) return [];
        
        const insights = [];
        const score = userReliability.overall_reliability_score;
        const flaggedRatio = userReliability.total_predictions > 0 
            ? (userReliability.flagged_predictions / userReliability.total_predictions) 
            : 0;

        if (score >= 90) {
            insights.push("🌟 Excellent! Your predictions are high quality.");
        } else if (score >= 70) {
            insights.push("👍 Good work! Keep up the quality content.");
        } else if (score >= 50) {
            insights.push("⚠️ Your content quality could be improved.");
        } else {
            insights.push("🚨 Focus on writing more thoughtful predictions.");
        }

        if (flaggedRatio > 0.3) {
            insights.push("⚠️ Many of your predictions have been flagged. Try to avoid spam or gibberish content.");
        } else if (flaggedRatio > 0.1) {
            insights.push("💡 Some content was flagged. Focus on meaningful predictions.");
        }

        if (userReliability.total_predictions < 5) {
            insights.push("📝 Share more predictions to build your reputation!");
        }

        return insights;
    };

    useEffect(() => {
        console.log('🎨 Chart Effect Triggered:', {
            isLoggedIn,
            hasUser: !!user,
            hasStats: !!userStats,
            dailyCount: userStats?.dailyActivity?.length,
            categoryCount: userStats?.categoryDistribution?.length
        });

        if (!isLoggedIn || !user || !userStats) {
            console.log('⏭️ Skipping charts - requirements not met');
            return;
        }

        // Destroy old charts first
        if (dailyActivityChartInstance.current) {
            dailyActivityChartInstance.current.destroy();
            dailyActivityChartInstance.current = null;
        }
        if (categoryChartInstance.current) {
            categoryChartInstance.current.destroy();
            categoryChartInstance.current = null;
        }

        const timer = setTimeout(() => {
            // ===== DAILY ACTIVITY CHART =====
            const dailyCanvas = dailyActivityChartRef.current;
            if (dailyCanvas && userStats.dailyActivity?.length > 0) {
                console.log('📊 Creating daily chart with', userStats.dailyActivity.length, 'days');
                
                const ctx = dailyCanvas.getContext('2d');
                const labels = userStats.dailyActivity.map(item => {
                    const d = new Date(item.date);
                    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                });
                const data = userStats.dailyActivity.map(item => parseInt(item.count));

                dailyActivityChartInstance.current = new Chart(ctx, {
                    type: 'bar',
                    data: {
                        labels: labels,
                        datasets: [{
                            label: 'Predictions',
                            data: data,
                            backgroundColor: 'rgba(200, 154, 26, 0.6)',
                            borderColor: 'rgba(200, 154, 26, 1)',
                            borderWidth: 2,
                            borderRadius: 6
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: { display: false },
                            tooltip: {
                                backgroundColor: 'rgba(0,0,0,0.8)',
                                titleColor: '#ffd700',
                                bodyColor: '#fff'
                            }
                        },
                        scales: {
                            y: {
                                beginAtZero: true,
                                ticks: { 
                                    color: '#999',
                                    stepSize: 1,
                                    callback: (v) => Number.isInteger(v) ? v : ''
                                },
                                grid: { color: 'rgba(255,255,255,0.1)' }
                            },
                            x: {
                                ticks: { color: '#999', maxRotation: 45, minRotation: 45 },
                                grid: { display: false }
                            }
                        }
                    }
                });
                console.log('✅ Daily chart created');
            } else {
                console.warn('⚠️ Cannot create daily chart - no data');
            }

            // ===== CATEGORY CHART =====
            const categoryCanvas = categoryChartRef.current;
            if (categoryCanvas && userStats.categoryDistribution?.length > 0) {
                console.log('🥧 Creating category chart with', userStats.categoryDistribution.length, 'categories');
                
                const ctx = categoryCanvas.getContext('2d');
                
                // Get top 8 categories, group rest as "Other"
                const sortedCategories = [...userStats.categoryDistribution].sort((a, b) => b.count - a.count);
                const top8 = sortedCategories.slice(0, 8);
                const rest = sortedCategories.slice(8);
                
                let labels = top8.map(item => item.category || 'Unknown');
                let data = top8.map(item => parseInt(item.count));
                
                if (rest.length > 0) {
                    const otherCount = rest.reduce((sum, item) => sum + parseInt(item.count), 0);
                    labels.push('Other');
                    data.push(otherCount);
                }

                const colors = [
                    'rgba(200, 154, 26, 0.8)',
                    'rgba(255, 99, 132, 0.8)',
                    'rgba(54, 162, 235, 0.8)',
                    'rgba(255, 206, 86, 0.8)',
                    'rgba(75, 192, 192, 0.8)',
                    'rgba(153, 102, 255, 0.8)',
                    'rgba(255, 159, 64, 0.8)',
                    'rgba(199, 199, 199, 0.8)',
                    'rgba(100, 100, 100, 0.8)'
                ];

                categoryChartInstance.current = new Chart(ctx, {
                    type: 'doughnut',
                    data: {
                        labels: labels,
                        datasets: [{
                            data: data,
                            backgroundColor: colors.slice(0, data.length),
                            borderColor: '#1a1a1a',
                            borderWidth: 2
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                position: 'bottom',
                                labels: { color: '#999', padding: 15, font: { size: 12 } }
                            },
                            tooltip: {
                                backgroundColor: 'rgba(0,0,0,0.8)',
                                titleColor: '#ffd700',
                                bodyColor: '#fff',
                                callbacks: {
                                    label: (ctx) => {
                                        const total = ctx.dataset.data.reduce((a,b) => a+b, 0);
                                        const pct = ((ctx.parsed / total) * 100).toFixed(1);
                                        return `${ctx.label}: ${ctx.parsed} (${pct}%)`;
                                    }
                                }
                            }
                        }
                    }
                });
                console.log('✅ Category chart created');
            } else {
                console.warn('⚠️ Cannot create category chart - no data');
            }
        }, 100);

        return () => {
            clearTimeout(timer);
            if (dailyActivityChartInstance.current) dailyActivityChartInstance.current.destroy();
            if (categoryChartInstance.current) categoryChartInstance.current.destroy();
        };
    }, [userStats, user, isLoggedIn]);

    useEffect(() => {
        if (!isLoggedIn) {
            destroyCharts();
        }
    }, [isLoggedIn, destroyCharts]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getCategoryIcon = (category) => {
        const iconMap = {
            'Technology': Rocket,
            'Politics': Users,
            'Economy': TrendingUp,
            'Environment': Globe,
            'Healthcare': Activity,
            'Society': Users,
            'Entertainment': Sparkles,
            'Sports': Target,
            'Trendphile': BarChart3,
            'Other': FileText
        };
        return iconMap[category] || FileText;
    };

const showFlagReason = (prediction) => {
    // Extract detailed reasoning if available
    const flagReasoning = prediction.flag_reasoning;
    
    let displayReason = prediction.flag_reason || 'No specific reason provided';
    let improvementTips = [];
    
    if (flagReasoning) {
        displayReason = flagReasoning.detailed_explanation || flagReasoning.reason || displayReason;
        improvementTips = flagReasoning.improvement_tips || [];
    }
    
    setFlagModal({
        isOpen: true,
        reason: displayReason,
        predictionText: prediction.prediction_text,
        improvements: improvementTips, // 🆕 Add improvement tips
        severity: flagReasoning?.severity || 'medium',
        category: flagReasoning?.category || 'quality',
        score: prediction.reliability_score || prediction.analysis_result?.score
    });
};


    const closeFlagModal = () => {
        setFlagModal({
            isOpen: false,
            reason: '',
            predictionText: ''
        });
    };

const showQualityModal = (prediction) => {
    console.log('🔍 Opening quality modal for prediction:', prediction);
    console.log('🔍 Analysis result:', prediction.analysis_result);
    console.log('🔍 Improvement suggestions:', prediction.suggestions);
    
    setQualityModal({
        isOpen: true,
        predictionData: prediction,
        suggestions: prediction.suggestions || []  // ✅ FIXED: Use prediction.suggestions
    });
};


    const closeQualityModal = () => {
        setQualityModal({ 
            isOpen: false, 
            predictionData: null, 
            suggestions: [] 
        });
    };

const handleCaptchaVerify = (token) => {
    console.log('✅ hCaptcha verified:', token);
    setCaptchaValue(token);
    setCaptchaError('');
};

const handleCaptchaExpire = () => {
    console.log('⏰ hCaptcha expired');
    setCaptchaValue(null);
    setCaptchaError('CAPTCHA expired, please verify again');
};

const handleCaptchaError = (error) => {
    console.log('❌ hCaptcha error:', error);
    setCaptchaValue(null);
    setCaptchaError('CAPTCHA failed, please try again');
};

const resetCaptcha = () => {
    if (captchaRef.current) {
        captchaRef.current.resetCaptcha();
    }
    setCaptchaValue(null);
    setCaptchaError('');
};


    // Dashboard loading state - when logged in but userStats not loaded yet
    if (isLoggedIn && !userStats && !showSuccessAnimation) {
        return (
            
                <div className="login-container">
                <div className="background-pattern"></div>
                <div className="background-gradient"></div>
                <div className="ambient-glow"></div>
                <div className="ambient-glow-2"></div>
                <div className="dashboard-loading">
                    <div className="loading-content">
                        <div className="loading-avatar">
                            <User size={60} />
                            <div className="avatar-glow"></div>
                        </div>
                        <h2 className="loading-title">Welcome back, {user?.username || user?.email?.split('@')[0] || localStorage.getItem('username') || 'User'}!</h2>
                        <div className="loading-steps">
                            <div className="loading-step active">
                                <div className="step-icon">
                                    <CheckCircle size={20} />
                                </div>
                                <span>Authentication successful</span>
                            </div>
                            <div className="loading-step active">
                                <div className="step-icon">
                                    <Loader className="step-spinner" size={20} />
                                </div>
                                <span>Loading your dashboard...</span>
                            </div>
                            <div className="loading-step">
                                <div className="step-icon">
                                    <BarChart3 size={20} />
                                </div>
                                <span>Preparing analytics</span>
                            </div>
                        </div>
                        <div className="loading-progress">
                            <div className="progress-bar">
                                <div className="progress-fill"></div>
                            </div>
                            <p className="loading-message">Setting up your personalized experience...</p>
                        </div>
                    </div>
                </div>
            </div>
            
        );
    }
    

    // Regular loading state
    if (isLoading && !showSuccessAnimation) {
        return (
            
                <div className="login-container">
                <div className="background-pattern"></div>
                <div className="loading-spinner">
                    <Loader className="spinner-icon" />
                    <p>Loading...</p>
                </div>
            </div>
            
        );
    }

    // Success animation for new accounts
    if (showSuccessAnimation) {
        return (
            
                <div className="login-container">
                <div className="background-pattern"></div>
                <div className="success-animation">
                    <div className="success-checkmark">
                        <CheckCircle size={80} />
                    </div>
                    <h2>Welcome to the Community!</h2>
                    <p>Your account has been created successfully</p>
                    <div className="success-loader">
                        <Loader className="spinner-icon" />
                        <span>Setting up your dashboard...</span>
                    </div>
                </div>
            </div>
            
        );
    }



    // ❌ REMOVED - This was blocking the main dashboard from loading
    // The loading state above already handles this case

    if (isLoggedIn && userStats) {
        const predictionsToShow = showAllPredictions ? allPredictions : recentPredictions;
        
        return (
            <div className="login-container">
                <div className="dashboard-section">
                    <div className="dashboard-header">
                        <div className="user-avatar">
                            <User className="avatar-icon" />
                        </div>
                        <div className="user-welcome">
                            <h1 className="dashboard-title">Welcome back, {user?.username || user?.email?.split('@')[0] || 'User'}!</h1>
                            <p className="dashboard-subtitle">
    {activeTab === 'overview' && !showAllPredictions && "Here's your prediction journey overview"}
    {activeTab === 'overview' && showAllPredictions && `Viewing all ${allPredictions.length} predictions`}
    {activeTab === 'referrals' && "Manage your referrals and track rewards"}
    {activeTab === 'tphile' && "View your TPHILE token balance and transaction history"}
</p>
                        </div>
                        <button onClick={handleLogout} className="logout-button">
                            <LogOut className="logout-icon" />
                            Logout
                        </button>
                    </div>

{/* Tab Navigation */}
<div className="dashboard-tabs">
    <button 
        className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
        onClick={() => handleTabChange('overview')}
    >
        <BarChart3 size={18} />
        <span>Overview</span>
    </button>
    <button 
        className={`tab-btn ${activeTab === 'referrals' ? 'active' : ''}`}
        onClick={() => handleTabChange('referrals')}
    >
        <Users size={18} />
        <span>Referrals</span>
    </button>
    <button 
        className={`tab-btn ${activeTab === 'tphile' ? 'active' : ''}`}
        onClick={() => handleTabChange('tphile')}
    >
        <Coins size={18} />
        <span>TPHILE</span>
    </button>
</div>
                    {/* 📊 OVERVIEW TAB */}
                    {activeTab === 'overview' && (
                    <>
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-icon">
                                <Target size={28} />
                            </div>
                            <div className="stat-content">
                                <h3>{userStats.totalPredictions}</h3>
                                <p>Total Predictions</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">
                                <BarChart3 size={28} />
                            </div>
                            <div className="stat-content">
                                <h3>{userStats.categoryDistribution.length}</h3>
                                <p>Categories Explored</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">
                                <Calendar size={28} />
                            </div>
                            <div className="stat-content">
                                <h3>{Math.max(...userStats.dailyActivity.map(d => d.count))}</h3>
                                <p>Best Day (predictions)</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">
                                <CalendarDays size={28} />
                            </div>
                            <div className="stat-content">
                                <h3>{new Date(user?.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</h3>
                                <p>Member Since</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">
                                <Shield size={28} />
                            </div>
                            <div className="stat-content">
                                <h3>
                                    {userReliability?.overall_reliability_score !== null && userReliability?.overall_reliability_score !== undefined 
                                        ? Math.round(userReliability.overall_reliability_score) 
                                        : 'N/A'
                                    }
                                </h3>
                                <p>Reliability Score(Max 100)</p>
                            </div>
                        </div>
                    </div>


                    <div className="dashboard-content">
                        <div className="predictions-section">
                            <div className="section-header">
                                <h3>
                                    <FileText className="section-icon" />
                                    {showAllPredictions ? 'All Your Predictions' : 'Recent Predictions'}
                                </h3>
                                <div className="header-actions">
                                    {showAllPredictions && (
                                        <button 
                                            onClick={() => setShowAllPredictions(false)}
                                            className="back-btn"
                                        >
                                            <ArrowLeft size={16} /> Back to Recent
                                        </button>
                                    )}
                                    <button 
                                        onClick={() => navigate('/submit')} 
                                        className="add-prediction-btn"
                                    >
                                        <Plus size={16} /> Add New
                                    </button>
                                </div>
                            </div>
                            {predictionsToShow.length === 0 ? (
                                <div className="no-predictions">
                                    <div className="no-predictions-icon">
                                        <Target size={48} />
                                    </div>
                                    <h4>No predictions yet!</h4>
                                    <p>Start sharing your vision of the future</p>
                                    <button 
                                        onClick={() => navigate('/submit')} 
                                        className="create-first-btn"
                                    >
                                        Create Your First Prediction
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {isLoadingAll && (
                                        <div className="loading-predictions">
                                            <Loader className="spinner-icon" />
                                            <p>Loading all predictions...</p>
                                        </div>
                                    )}
                                    <div className={`predictions-list ${showAllPredictions ? 'expanded' : ''}`}>
                                        {predictionsToShow.map((prediction, index) => {
                                            const CategoryIcon = getCategoryIcon(prediction.category);
                                            return (
                                                <div key={prediction.id || index} className="prediction-card">
                                                    <div className="prediction-header">
                                                        <div className="prediction-meta">
                                                            <span className="category-badge">
                                                                <CategoryIcon className="category-icon" size={16} />
                                                                {prediction.category}
                                                            </span>
                                                            <span className="prediction-date">
                                                                {formatDate(prediction.timestamp)}
                                                            </span>
                                                            {prediction.reliability_score !== null && 
                                                            prediction.reliability_score !== undefined && (
                                                                <span className={`reliability-badge ${getReliabilityClass(prediction.reliability_score)}`}>
                                                                    <Award size={12} />
                                                                    {Math.round(prediction.reliability_score)}% Quality
                                                                </span>
                                                            )}
                                                            {(prediction.reliability_score === null || prediction.reliability_score === undefined) && (
                                                                <span className="analyzing-badge">
                                                                    <Loader size={12} />
                                                                    Analyzing...
                                                                </span>
                                                            )}
                                                        {prediction.is_flagged && (
                                                            <span 
                                                                className={`flagged-badge clickable ${prediction.flag_reasoning?.severity || 'medium'}`}
                                                                onClick={() => showFlagReason(prediction)}
                                                                title="Click to see why this was flagged"
                                                            >
                                                                <AlertCircle size={12} />
                                                                Flagged ({prediction.reliability_score || prediction.analysis_result?.score}/100)
                                                            </span>
                                                        )}

                                                        </div>
                                                        <div className="prediction-actions">
                                                            <div className="visibility-badge">
                                                                <span className={`visibility-indicator ${prediction.visibility ? 'public' : 'private'}`}>
                                                                    {prediction.visibility ? <Globe size={14} /> : <EyeOff size={14} />}
                                                                    {prediction.visibility ? 'Public' : 'Private'}
                                                                </span>
                                                        </div>
{((prediction.reliability_score !== null && prediction.reliability_score !== undefined && prediction.reliability_score < 70) ||
  (prediction.analysis_result?.score !== null && prediction.analysis_result?.score !== undefined && prediction.analysis_result?.score < 70)) && (

                                                            <button 
                                                                onClick={() => showQualityModal(prediction)}
                                                                className="quality-help-btn"
                                                                title="View quality improvement suggestions"
                                                            >
                                                                <Shield size={16} />
                                                                Improve Quality
                                                            </button>
                                                        )}
                                                        </div>

                                                    </div>
                                                    <div className="prediction-content">
                                                        <p>{prediction.prediction_text}</p>
                                                        {(prediction.text_quality_score || prediction.sentiment_score !== undefined) && (
                                                            <div className="quality-indicators">
                                                                {prediction.text_quality_score && (
                                                                    <div className="quality-metric">
                                                                        <span className="metric-label">Text Quality:</span>
                                                                        <div className="metric-bar">
                                                                            <div 
                                                                                className="metric-fill" 
                                                                                style={{ width: `${prediction.text_quality_score * 100}%` }}
                                                                            ></div>
                                                                        </div>
                                                                        <span className="metric-value">{Math.round(prediction.text_quality_score * 100)}%</span>
                                                                    </div>
                                                                )}
                                                                {prediction.sentiment_score !== undefined && (
                                                                    <div className="quality-metric">
                                                                        <span className="metric-label">Sentiment:</span>
                                                                        <span className={`sentiment-badge ${prediction.sentiment_score > 0 ? 'positive' : prediction.sentiment_score < 0 ? 'negative' : 'neutral'}`}>
                                                                            {prediction.sentiment_score > 0.1 ? '😊 Positive' : 
                                                                             prediction.sentiment_score < -0.1 ? '😔 Negative' : '😐 Neutral'}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>


{qualityModal.isOpen && (
    <div className="quality-modal-overlay" onClick={closeQualityModal}>
        <div className="quality-modal" onClick={(e) => e.stopPropagation()}>
            <div className="quality-modal-header">
                <h3 className="quality-modal-title">
                    <Shield size={24} />
                    Quality Analysis
                    {qualityModal.predictionData?.flag_reasoning?.ai_generated && (
                        <span className="ai-badge">🤖 AI-Powered</span>
                    )}
                </h3>
                <button onClick={closeQualityModal} className="quality-modal-close">
                    <X size={20} />
                </button>
            </div>
            <div className="quality-modal-content">
                {qualityModal.predictionData && (
                    <>
                        {/* 🎯 ENHANCED QUALITY SCORE SECTION */}
                        <div className="quality-score-section">
                        <div className={`quality-score ${
                            (qualityModal.predictionData.analysis_result?.score || qualityModal.predictionData.reliability_score) >= 70 ? 'high' : 
                            (qualityModal.predictionData.analysis_result?.score || qualityModal.predictionData.reliability_score) >= 40 ? 'medium' : 'low'
                        }`}>
                            {Math.round(
                                qualityModal.predictionData.analysis_result?.score || 
                                qualityModal.predictionData.reliability_score || 0
                            )}%
                        </div>


                        <div className="quality-status">
                            {qualityModal.predictionData.is_flagged ? (
                                <span className="flagged-status">
                                    🚨 Flagged for Review
                                </span>
                            ) : ((qualityModal.predictionData.analysis_result?.score || qualityModal.predictionData.reliability_score) < 70) ? (
                                'Needs Improvement'
                            ) : (
                                'Good Quality'
                            )}
                        </div>
                        

                            
                            {/* 🆕 AI-POWERED FLAG REASONING */}
                            {qualityModal.predictionData.flag_reasoning && (
                                <div className="flag-reasoning-section">
                                    <div className={`flag-severity ${qualityModal.predictionData.flag_reasoning.severity}`}>
                                        <strong>Issue:</strong> {qualityModal.predictionData.flag_reasoning.reason}
                                    </div>
                                    <div className="flag-explanation">
                                        {qualityModal.predictionData.flag_reasoning.detailed_explanation}
                                    </div>
                                    {qualityModal.predictionData.flag_reasoning.category && (
                                        <div className="flag-category">
                                            <span className="category-badge">
                                                {qualityModal.predictionData.flag_reasoning.category.toUpperCase()}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* Fallback to old flag_reason if no new reasoning */}
                            {!qualityModal.predictionData.flag_reasoning && qualityModal.predictionData.flag_reason && (
                                <div className="quality-description">
                                    {qualityModal.predictionData.flag_reason}
                                </div>
                            )}
                        </div>

{/* 🚀 ENHANCED SUGGESTIONS SECTION */}
<div className="suggestions-section">
    <h4 className="suggestions-title">
        💡 Improvement Suggestions
        {qualityModal.predictionData.flag_reasoning?.ai_generated && (
            <span className="ai-suggestions-badge">AI-Generated</span>
        )}
    </h4>
    
    {qualityModal.predictionData.reliability_score < 70 ? (
        <div className="suggestions-list">
            {/* 🆕 AI-POWERED SPECIFIC SUGGESTIONS */}
            {qualityModal.predictionData.flag_reasoning?.improvement_tips?.map((tip, index) => (
                <div key={index} className="suggestion-item ai-suggestion">
                    <div className="suggestion-icon-wrapper">
                        🤖
                    </div>
                    <span>{tip}</span>
                </div>
            ))}
            
            {/* 🔄 ENHANCED GENERAL SUGGESTIONS - FIXED */}
            {qualityModal.suggestions?.map((suggestion, index) => (
                <div key={`general-${index}`} className={`suggestion-item ${suggestion.priority || 'medium'}-priority`}>
                    <div className="suggestion-icon-wrapper">
                        {suggestion.icon || '💡'}
                    </div>
                    <div className="suggestion-content">
                        {suggestion.title && <strong>{suggestion.title}</strong>}
                        {suggestion.description && <p>{suggestion.description}</p>}
                        {/* Handle if suggestion is just a string */}
                        {typeof suggestion === 'string' && <span>{suggestion}</span>}
                    </div>
                    {suggestion.category && (
                        <span className="suggestion-tag">{suggestion.category}</span>
                    )}
                </div>
            ))}
            
        </div>
    ) : (
        <div className="no-suggestions">
            <div className="success-icon">🎉</div>
            <p>Great job! Your prediction meets our quality standards.</p>
        </div>
    )}
</div>


                        {/* 🎨 ENHANCED MODAL ACTIONS */}
                        <div className="modal-actions">
                            <button onClick={closeQualityModal} className="btn-secondary">
                                Close
                            </button>
                            {((qualityModal.predictionData.reliability_score !== null && 
   qualityModal.predictionData.reliability_score !== undefined && 
   qualityModal.predictionData.reliability_score < 70) ||
  (qualityModal.predictionData.analysis_result?.score !== null && 
   qualityModal.predictionData.analysis_result?.score !== undefined && 
   qualityModal.predictionData.analysis_result?.score < 70)) && (

                                <button 
                                    onClick={() => {
                                        closeQualityModal();
                                        navigate('/submit');
                                    }} 
                                    className="btn-primary"
                                >
                                    Create Better Prediction
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    </div>
)}

                                </>
                            )}
                            {!showAllPredictions && recentPredictions.length > 0 && (
                                <div className="view-all-container">
                                    <button 
                                        onClick={handleViewAllPredictions} 
                                        className="view-all-btn"
                                    >
                                        View All {allPredictions.length} Predictions →
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="charts-container">
                            <div className="charts-grid">
                                <div className="chart-box">
                                    <div className="chart-header">
                                        <h3>
                                            <BarChart3 className="chart-icon" />
                                            Daily Activity (Last 30 Days)
                                        </h3>
                                    </div>
                                    <div className="chart-wrapper">
                                        <canvas ref={dailyActivityChartRef}></canvas>
                                    </div>
                                </div>
                                <div className="chart-box">
                                    <div className="chart-header">
                                        <h3>
                                            <PieChart className="chart-icon" />
                                            Prediction Categories
                                        </h3>
                                    </div>
                                    <div className="chart-wrapper">
                                        <canvas ref={categoryChartRef}></canvas>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    </>
                    )}


{/* 👥 REFERRALS TAB */}
{activeTab === 'referrals' && (
    <ReferralSystem />
)}

{/* 💰 TPHILE TAB */}
{activeTab === 'tphile' && (() => {
    const activeToken = token || localStorage.getItem('token') || sessionStorage.getItem('token');
    console.log('🔍 TPHILE Tab - Token Check:', {
        contextToken: token ? 'Present' : 'Missing',
        localStorageToken: localStorage.getItem('token') ? 'Present' : 'Missing',
        sessionStorageToken: sessionStorage.getItem('token') ? 'Present' : 'Missing',
        activeToken: activeToken ? 'Present' : 'Missing',
        userId: user?.id
    });
    
    return (
        <TokenDashboard 
            userToken={activeToken}
            userId={user?.id}
        />
    );
})()}

</div>
            </div>
        );
    }

    if (verificationState.showEmailSent) {
        return (
            <div className="login-container">
                <div className="background-pattern"></div>
                <div className="background-gradient"></div>
                <div className="ambient-glow"></div>
                <div className="ambient-glow-2"></div>
                <div className="verification-container">
                    <div className="verification-success">
                        <div className="verification-icon">
                            <Mail size={60} className="mail-icon" />
                            <div className="success-ring"></div>
                        </div>
                        <h2 className="verification-title">Check Your Email</h2>
                        <div className="verification-message">
                            We've sent a verification link to<br />
                            <strong>{verificationState.email}</strong>
                        </div>
                        <div className="verification-steps">
                            <div className="step">
                                <div className="step-number">1</div>
                                <span>Open your email inbox</span>
                            </div>
                            <div className="step">
                                <div className="step-number">2</div>
                                <span>Click the verification link</span>
                            </div>
                            <div className="step">
                                <div className="step-number">3</div>
                                <span>Complete your registration</span>
                            </div>
                        </div>
                        {verificationState.message && (
                            <div className="verification-note">
                                <Clock size={16} /> {verificationState.message}
                            </div>
                        )}
                        <div className="verification-actions">
                            <button 
                                className="resend-btn"
                                onClick={handleResendVerification}
                                disabled={verificationState.isResending}
                            >
                                {verificationState.isResending ? (
                                    <>
                                        <Loader size={20} className="spinning" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw size={20} />
                                        Resend Email
                                    </>
                                )}
                            </button>
                            <button 
                                className="back-to-login-btn"
                                onClick={() => {
                                    setVerificationState({
                                        showEmailSent: false,
                                        showResendOption: false,
                                        isResending: false,
                                        email: '',
                                        username: '',
                                        message: ''
                                    });
                                    setIsRegistering(false);
                                    setCurrentStep(1);
                                }}
                            >
                                <ArrowLeft size={18} />
                                Back to Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (verificationState.showResendOption) {
        return (
            <div className="login-container">
                <div className="background-pattern"></div>
                <div className="background-gradient"></div>
                <div className="ambient-glow"></div>
                <div className="ambient-glow-2"></div>
                <div className="verification-container">
                    <div className="verification-success">
                        <div className="verification-icon">
                            <AlertCircle size={60} className="alert-icon" />
                            <div className="warning-ring"></div>
                        </div>
                        <h2 className="verification-title">Email Verification Required</h2>
                        <div className="verification-message">
                            {verificationState.message || 'Please verify your email address to continue.'}
                        </div>
                        <div className="verification-steps">
                            <div className="step">
                                <div className="step-number">1</div>
                                <span>Check your email for verification link</span>
                            </div>
                            <div className="step">
                                <div className="step-number">2</div>
                                <span>Click the link to verify your account</span>
                            </div>
                            <div className="step">
                                <div className="step-number">3</div>
                                <span>Return here to access your account</span>
                            </div>
                        </div>
                        <div className="verification-actions">
                            <button 
                                className="resend-btn"
                                onClick={handleResendVerification}
                                disabled={verificationState.isResending}
                            >
                                {verificationState.isResending ? (
                                    <>
                                        <Loader size={20} className="spinning" />
                                        Sending New Link...
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw size={20} />
                                        Resend Verification Email
                                    </>
                                )}
                            </button>
                            <button 
                                className="back-to-login-btn"
                                onClick={() => {
                                    setVerificationState({
                                        showEmailSent: false,
                                        showResendOption: false,
                                        isResending: false,
                                        email: '',
                                        username: '',
                                        message: ''
                                    });
                                    setIsRegistering(false);
                                    setCurrentStep(1);
                                }}
                            >
                                <ArrowLeft size={18} />
                                Back to Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-container">
            <div className="background-pattern"></div>
            <div className="background-gradient"></div>
            <div className="ambient-glow"></div>
            <div className="ambient-glow-2"></div>
            <div className="login-box">
                {isRegistering && (
                    <div className="progress-indicator">
                        <div className="progress-steps">
                            <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                                <span className="step-number">1</span>
                                <span className="step-label">Email</span>
                            </div>
                            <div className="progress-line"></div>
                            <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                                <span className="step-number">2</span>
                                <span className="step-label">Username</span>
                            </div>
                            <div className="progress-line"></div>
                            <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
                                <span className="step-number">3</span>
                                <span className="step-label">Password</span>
                            </div>
                        </div>
                        <div className="progress-bar">
                            <div 
                                className="progress-fill" 
                                style={{ width: `${(currentStep - 1) * 50}%` }}
                            ></div>
                        </div>
                    </div>
                )}
                <div className="login-header">
                    <div className="login-icon">
                        <Eye size={48} />
                    </div>
                    <h2 className="login-title">
                        {isRegistering 
                            ? (currentStep === 1 ? 'Join the Vision' : 
                               currentStep === 2 ? 'Choose Your Identity' : 
                               'Secure Your Account')
                            : 'Welcome Back'
                        }
                    </h2>
                    <p className="login-subtitle">
                        {isRegistering 
                            ? (currentStep === 1 ? 'Enter your email to get started' :
                               currentStep === 2 ? 'Pick a unique username for your predictions' :
                               'Create a strong password to protect your account')
                            : 'Continue your prediction journey'
                        }
                    </p>
                </div>
                <form onSubmit={handleSubmit} className="login-form">
    {/* 🚨 ELEGANT ERROR MESSAGE */}
    {errorMessage && (
        <div className="elegant-error-message">
            <div className="error-icon-wrapper">
                <AlertTriangle className="error-icon" />
            </div>
            <div className="error-content">
                <h4 className="error-title">Login Failed</h4>
                <p className="error-description">{errorMessage}</p>
            </div>
            <button 
                onClick={() => setErrorMessage('')} 
                className="error-dismiss-btn"
                aria-label="Dismiss error"
            >
                <X className="dismiss-icon" />
            </button>
        </div>
    )}
    
    {(!isRegistering || currentStep === 1) && (
        <div className="step-container">
            <div className={`input-group ${emailFocused || email ? 'focused' : ''} ${emailValidation.message ? (emailValidation.isValid ? 'valid' : 'invalid') : ''}`}>
                <div className="input-icon">
                    <Mail size={16} />
                </div>
                <div className="input-wrapper">
                    <input
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                        onFocus={() => setEmailFocused(true)}
                        onBlur={() => setEmailFocused(false)}
                        className="login-input"
                        required
                        id="email"
                    />
                    <label htmlFor="email" className="floating-label">
                        Email address
                    </label>
                </div>
                <div className="input-status">
                    {emailValidation.isChecking && <Loader className="status-spinner" size={16} />}
                    {!emailValidation.isChecking && emailValidation.message && (
                        emailValidation.isValid ? 
                        <Check className="status-valid" size={16} /> : 
                        <X className="status-invalid" size={16} />
                    )}
                </div>
            </div>
            {emailValidation.message && (
                <div className={`validation-message ${emailValidation.isValid ? 'valid' : 'invalid'}`}>
                    {emailValidation.message}
                </div>
            )}
        </div>
    )}

                    {isRegistering && currentStep === 2 && (
                        <div className="step-container">
                            <div className={`input-group ${usernameFocused || username ? 'focused' : ''} ${usernameValidation.message ? (usernameValidation.isValid ? 'valid' : 'invalid') : ''}`}>
                                <div className="input-icon">
                                    <User size={20} />
                                </div>
                                <div className="input-wrapper">
                                    <input
                                        type="text"
                                        value={username}
                                        onChange={handleUsernameChange}
                                        onFocus={() => setUsernameFocused(true)}
                                        onBlur={() => setUsernameFocused(false)}
                                        className="login-input"
                                        required
                                        id="username"
                                    />
                                    <label htmlFor="username" className="floating-label">
                                        Choose your username
                                    </label>
                                </div>
                                <div className="input-status">
                                    {usernameValidation.isChecking && <Loader className="status-spinner" size={16} />}
                                    {!usernameValidation.isChecking && usernameValidation.message && (
                                        usernameValidation.isValid ? 
                                        <Check className="status-valid" size={16} /> : 
                                        <X className="status-invalid" size={16} />
                                    )}
                                </div>
                            </div>
                            <div className="format-requirements">
                                <AlertCircle size={14} />
                                <span>3-20 characters, letters, numbers and underscores only</span>
                            </div>
                            {usernameValidation.message && (
                                <div className={`validation-message ${usernameValidation.isValid ? 'valid' : 'invalid'}`}>
                                    {usernameValidation.message}
                                </div>
                            )}
                            {suggestedUsernames.length > 0 && !username && (
                                <div className="username-suggestions">
                                    <p className="suggestions-label">Suggestions based on your email:</p>
                                    <div className="suggestions-list">
                                        {suggestedUsernames.map((suggestion, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                onClick={() => {
                                                    setUsername(suggestion);
                                                    validateUsername(suggestion);
                                                }}
                                                className="suggestion-btn"
                                            >
                                                {suggestion}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {(!isRegistering || currentStep === 3) && (
                        <div className="step-container">
                            <div className={`input-group ${passwordFocused || password ? 'focused' : ''} ${isRegistering && passwordValidation.message ? (passwordValidation.isValid ? 'valid' : 'invalid') : ''}`}>
                                <div className="input-icon">
                                    <Lock size={16} />
                                </div>
                                <div className="input-wrapper">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={handlePasswordChange}
                                        onFocus={() => setPasswordFocused(true)}
                                        onBlur={() => setPasswordFocused(false)}
                                        className="login-input"
                                        required
                                        id="password"
                                    />
                                    <label htmlFor="password" className="floating-label">
                                        Password
                                    </label>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="password-toggle"
                                >
                                    {showPassword ? <EyeOff size={16} /> : <EyeIcon size={16} />}
                                </button>
                                {isRegistering && passwordValidation.message && (
                                    <div className="input-status">
                                        {passwordValidation.isValid ? 
                                        <Check className="status-valid" size={16} /> : 
                                        <X className="status-invalid" size={16} />}
                                    </div>
                                )}
                            </div>
                            {isRegistering && password && (
                                <div className="password-strength">
                                    <div className="strength-bar">
                                        <div 
                                            className={`strength-fill ${passwordValidation.strength}`}
                                            style={{ 
                                                width: passwordValidation.strength === 'weak' ? '25%' : 
                                                       passwordValidation.strength === 'medium' ? '65%' : '100%' 
                                            }}
                                        ></div>
                                    </div>
                                    <div className="strength-text">
                                        <span className={`strength-label ${passwordValidation.strength}`}>
                                            {passwordValidation.strength.toUpperCase()}
                                        </span>
                                        <span className="strength-message">
                                            {passwordValidation.message}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
{(!isRegistering || currentStep === 3) && (
    <div className="form-options">
        <label className="remember-me">
            <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="remember-checkbox"
            />
            <span className="checkmark">
                {rememberMe ? <CheckCircle size={16} /> : <Circle size={16} />}
            </span>
            <span className="remember-label">Remember me</span>
        </label>

    </div>
)}
{/* ⭐ ADD THIS ENTIRE BLOCK HERE ⭐ */}
{/* Google Sign-In - Only for Login */}
{/* Google Sign-In - Only for Login */}
{!isRegistering && (!isLoggedIn) && (
    <>
        <GoogleSignIn 
            onError={setErrorMessage} 
            onLogin={async (googleData) => {
                console.log('🎉 Google login response:', googleData);
                
                try {
                    // Show loading animation immediately
                    setShowLoadingAnimation(true);
                    console.log('🌙 Showing moon animation…');

                    // 1) Store auth data in both storages
                    localStorage.setItem('token', googleData.token);
                    localStorage.setItem('user', JSON.stringify(googleData.user));
                    sessionStorage.setItem('token', googleData.token);
                    sessionStorage.setItem('user', JSON.stringify(googleData.user));
                    console.log('💾 Auth data saved to storage');

                    // 2) Update AuthContext and wait
                    await loginWithGoogle(googleData, rememberMe);
                    console.log('✅ AuthContext updated');

                    // 3) Update local state
                    setIsLoggedIn(true);
                    setUser(googleData.user);

                    // 4) Fetch user data before redirect
                    if (googleData.token) {
                        console.log('📡 Fetching user data before redirect…');
                        await fetchUserData(googleData.token);
                        console.log('✅ User data fetched');
                    }

                    // 5) Small delay to allow React state propagation
                    await new Promise(resolve => setTimeout(resolve, 500));

                    // 6) Stay on this page (account view) — no redirect
                    console.log('✅ Login complete, staying on account view');
                    setShowLoadingAnimation(false);
                } catch (error) {
                    console.error('❌ Error processing Google login:', error);
                    setShowLoadingAnimation(false);
                    setErrorMessage('Failed to complete Google sign-in');
                    setIsLoading(false);
                }
            }}
        />

        <div className="divider">
        </div>
    </>
)}

                    {/* 🔒 hCaptcha Security Verification - Only for Login */}
{!isRegistering && !isLoggedIn && (
    <>
        <div className="security-indicator">
            <Shield size={16} />
            <span>Security verification required</span>
        </div>
        <div className="hcaptcha-container">
<HCaptcha
    sitekey={process.env.REACT_APP_HCAPTCHA_SITE_KEY || process.env.REACT_APP_HCAPTCHA_SITEKEY || ''}
    onVerify={handleCaptchaVerify}
    onExpire={handleCaptchaExpire}
    onError={(err) => {
        console.warn('hCaptcha error:', err);
        handleCaptchaError(err);
    }}
    ref={captchaRef}
    theme="dark"
    size="normal"
    className="custom-hcaptcha"
/>

            {captchaError && (
                <div className="hcaptcha-error">
                    <AlertCircle size={16} />
                    <span>{captchaError}</span>
                </div>
            )}
            {captchaValue && (
                <div className="hcaptcha-success">
                    <CheckCircle size={16} />
                    <span>Security verification completed</span>
                </div>
            )}
        </div>
    </>
)}

                    <div className="form-actions">
                        {isRegistering && currentStep > 1 && (
                            <button 
                                type="button" 
                                onClick={prevStep} 
                                className="prev-step-btn"
                            >
                                <ChevronLeft size={16} />
                                Back
                            </button>
                        )}
                        {isRegistering && currentStep < 3 ? (
                            <button 
                                type="button" 
                                onClick={nextStep} 
                                className="next-step-btn"
                                disabled={
                                    (currentStep === 1 && !canProceedToStep2) ||
                                    (currentStep === 2 && !canProceedToStep3)
                                }
                            >
                                Continue
                                <ChevronRight size={16} />
                            </button>
                        ) : (
                            <button 
                                type="submit" 
                                className="login-button" 
                                disabled={isLoading || (isRegistering && !canSubmit)}
                            >
                                {isLoading ? (
                                    <>
                                        <Loader className="button-spinner" />
                                        {isRegistering ? 'Creating Account...' : 'Signing In...'}
                                    </>
                                ) : (
                                    <>
                                        <span className="button-icon">
                                            {isRegistering ? <Rocket size={20} /> : <Sparkles size={20} />}
                                        </span>
                                        {isRegistering ? 'Create Account' : 'Sign In'}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
</form>

{/* Forgot Password Modal */}
{showForgotPassword && (
    <div className="forgot-password-modal-overlay" onClick={() => setShowForgotPassword(false)}>
        <div className="forgot-password-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
                <h3>Reset Your Password</h3>
                <button 
                    onClick={() => setShowForgotPassword(false)}
                    className="modal-close"
                >
                    <X size={20} />
                </button>
            </div>
            <div className="modal-content">
                {/* Email Animation */}
                {showEmailAnimation && (
                    <div className="email-animation-overlay">
                        <div className="email-animation">
                            <div className="mailbox">
                                <div className="mailbox-body"></div>
                                <div className="mailbox-flag"></div>
                                <div className="mailbox-post"></div>
                            </div>
                            <div className="envelope">
                                <div className="envelope-flap"></div>
                                <div className="envelope-body"></div>
                            </div>
                            <div className="animation-text">
                                <h3>Email Sent!</h3>
                                <p>Check your inbox for the reset link</p>
                            </div>
                        </div>
                    </div>
                )}
                
                <p>Enter your email address and we'll send you a link to reset your password.</p>
                <form onSubmit={handleForgotPassword} className="forgot-password-form">
                    <div className="input-group focused">
                        <div className="input-icon">
                            <Mail size={16} />
                        </div>
                        <div className="input-wrapper">
                            <input
                                type="email"
                                value={resetEmail}
                                onChange={(e) => setResetEmail(e.target.value)}
                                className="form-input"
                                placeholder="Email address"
                                required
                            />
                        </div>
                    </div>
                    {resetMessage && (
                        <div className={`reset-message ${resetMessage.includes('sent') ? 'success' : 'error'}`}>
                            {resetMessage}
                        </div>
                    )}
                    <button 
                        type="submit" 
                        className="reset-submit-btn" 
                        disabled={isLoading || showEmailAnimation}
                    >
                        {isLoading ? (
                            <>
                                <Loader className="button-spinner" />
                                Sending...
                            </>
                        ) : (
                            <>
                                <Send size={16} />
                                Send Reset Link
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>
    </div>
)}


<div className="form-footer">
    {/* ADD THIS BUTTON - This is what was missing! */}
    {!isRegistering && (
        <button 
            type="button"
            className="forgot-password-simple"
            onClick={() => setShowForgotPassword(true)}
        >
            Forgot Password?
        </button>
    )}
    
    <p className="toggle-link" onClick={toggleRegistrationMode}>
        {isRegistering 
            ? 'Already have an account? Sign in' 
            : 'New to predictions? Create account'
        }
    </p>
</div>


                {!isRegistering && (
                    <div className="login-benefits">
                        <h4>Why Use Our Platform?</h4>
                        <div className="benefits-list">
                            <div className="benefit-item">
                                <span className="benefit-icon">
                                    <Target size={20} />
                                </span>
                                <span>Track prediction quality</span>
                            </div>
                            <div className="benefit-item">
                                <span className="benefit-icon">
                                    <BarChart3 size={20} />
                                </span>
                                <span>Access detailed analytics</span>
                            </div>
                            <div className="benefit-item">
                                <span className="benefit-icon">
                                    <Award size={20} />
                                </span>
                                <span>Share your vision of tomorrow</span>
                            </div>
                        </div>
                    </div>
                )}
                {showLoadingAnimation && <LoadingAnimation />}
            </div>
        </div>
    );
}

export default Login;
